import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';

import {
  NonConformitySigner,
  SignNonConformityInput,
  SignatureStyle,
} from '../../../services/qualityControlService';

// Mesmos estilos da assinatura do relatório de despesas (fontes carregadas
// em public/index.html).
export const SIGNATURE_STYLES: {
  value: SignatureStyle;
  label: string;
  font: string;
}[] = [
  { value: 'cursive-1', label: 'Cursiva 1', font: 'Dancing Script, cursive' },
  { value: 'cursive-2', label: 'Cursiva 2', font: 'Pacifico, cursive' },
  { value: 'cursive-3', label: 'Cursiva 3', font: 'Great Vibes, cursive' },
  { value: 'elegant-1', label: 'Elegante 1', font: 'Playfair Display, serif' },
  {
    value: 'elegant-2',
    label: 'Elegante 2',
    font: 'Cormorant Garamond, serif',
  },
];

export function signatureFont(style: SignatureStyle | null): string {
  return (
    SIGNATURE_STYLES.find(item => item.value === style)?.font ??
    SIGNATURE_STYLES[0].font
  );
}

function formatCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);

  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  signer: NonConformitySigner;
  code: string;
  // Nome de quem assina (quem abriu ou o responsável).
  defaultName: string;
  // Quem assina não é o usuário logado: pede o login e a senha dessa pessoa.
  requireCredentials?: boolean;
  loggedUserName?: string;
  isSubmitting: boolean;
  onSubmit: (input: SignNonConformityInput) => void;
}

export const SignatureModal: React.FC<SignatureModalProps> = ({
  isOpen,
  onClose,
  signer,
  code,
  defaultName,
  requireCredentials = false,
  loggedUserName,
  isSubmitting,
  onSubmit,
}) => {
  const [name, setName] = useState(defaultName);
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [cpf, setCpf] = useState('');
  const [style, setStyle] = useState<SignatureStyle>('cursive-1');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setNickname('');
      setPassword('');
      setCpf('');
      setStyle('cursive-1');
      setAgreed(false);
    }
  }, [defaultName, isOpen]);

  const declaration =
    signer === 'opener'
      ? `Declaro que registrei a ${code} e que as informações conferem.`
      : `Declaro que tomei ciência da ${code} e do plano de ação sob minha responsabilidade.`;
  const canSubmit =
    name.trim().length > 0 &&
    cpf.replace(/\D/g, '').length === 11 &&
    agreed &&
    (!requireCredentials ||
      (nickname.trim().length > 0 && password.length > 0));

  const handleSubmit = () =>
    onSubmit({
      name: name.trim(),
      cpf,
      style,
      ...(requireCredentials ? { nickname: nickname.trim(), password } : {}),
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent mx="4">
        <ModalHeader>
          {signer === 'opener' ? 'Assinar registro' : 'Assinar ciência'} —{' '}
          {code}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {requireCredentials && (
            <Box
              borderWidth="1px"
              borderColor="blue.400"
              borderRadius="md"
              p="3"
              mb="4"
            >
              <Text fontSize="sm" mb="3">
                {loggedUserName
                  ? `Você está logado como ${loggedUserName}. `
                  : ''}
                Para assinar como <strong>{defaultName}</strong>, essa pessoa
                informa aqui o usuário e a senha do SAMASC dela. A sua sessão
                não muda.
              </Text>
              <Text fontSize="sm" mb="1">
                Usuário do SAMASC de {defaultName}
              </Text>
              <Input
                value={nickname}
                onChange={e => setNickname(e.target.value)}
                autoComplete="off"
                mb="3"
              />
              <Text fontSize="sm" mb="1">
                Senha
              </Text>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </Box>
          )}

          <Text fontSize="sm" mb="1">
            Nome da assinatura
          </Text>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={255}
            mb="3"
          />

          <Text fontSize="sm" mb="1">
            CPF
          </Text>
          <Input
            value={cpf}
            onChange={e => setCpf(formatCpf(e.target.value))}
            placeholder="000.000.000-00"
            inputMode="numeric"
            mb="1"
          />
          <Text fontSize="xs" color="gray.400" mb="3">
            Aparece mascarado no PDF (***.456.789-**).
          </Text>

          <Text fontSize="sm" mb="1">
            Estilo da assinatura
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="2" mb="3">
            {SIGNATURE_STYLES.map(item => (
              <Box
                key={item.value}
                as="button"
                type="button"
                onClick={() => setStyle(item.value)}
                borderWidth="2px"
                borderColor={style === item.value ? 'blue.400' : 'gray.600'}
                borderRadius="md"
                px="3"
                py="2"
                textAlign="left"
              >
                <Text fontSize="xs" color="gray.400">
                  {item.label}
                </Text>
                <Text fontFamily={item.font} fontSize="xl" isTruncated>
                  {name.trim() || 'Sua assinatura'}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <Checkbox
            isChecked={agreed}
            onChange={e => setAgreed(e.target.checked)}
          >
            <Text fontSize="sm">{declaration}</Text>
          </Checkbox>
        </ModalBody>
        <ModalFooter gridGap="3">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="green"
            isDisabled={!canSubmit}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            Assinar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
